import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Table, Form, Modal, Alert, Badge, InputGroup } from 'react-bootstrap';
import api from '../api';
import { io } from 'socket.io-client';

const API_URL = process.env.REACT_APP_API_URL || '';

function Devices({ user }) {
  const [devices, setDevices] = useState([]);
  const [locations, setLocations] = useState([]);
  
  // Modals state
  const [showModal, setShowModal] = useState(false);
  const [editingDevice, setEditingDevice] = useState(null);
  const [showTokenModal, setShowTokenModal] = useState(false);
  const [selectedToken, setSelectedToken] = useState('');
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [alertDeviceId, setAlertDeviceId] = useState(null);

  // Alert Data State - ΠΡΟΣΤΕΘΗΚΕ ΤΟ reportIntervalSec
  const [alertData, setAlertData] = useState({
    minTemp: 0,
    maxTemp: 10,
    alertsEnabled: false,
    doorAlertsEnabled: false,
    doorOpenDelay: 5,
    localAlarmThreshold: 3,
    localAlarmDurationSec: 10,
    tempDelayEnabled: false,
    tempAlertDelay: 15,
    emailAlertsEnabled: false,
    discordAlertsEnabled: false,
    reportIntervalSec: 300 // Προεπιλογή 5 λεπτά
  });

  // Form Data State
  const [formData, setFormData] = useState({
    locationId: '',
    name: '',
    deviceType: 'fridge',
    shellyId: '',
    description: '',
    hardwareType: 'shelly',
    pinConfig: { sensorType: 'dallas', hasDoor: false }
  });

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchDevices();
    fetchLocations();

    const socket = io(API_URL);
    socket.on('temperature_update', (data) => {
      setDevices(prev => prev.map(d => d.id === data.deviceId ? ({
        ...d,
        lastTemperature: data.temperature,
        lastHumidity: data.humidity,
        lastDoorOpen: data.doorOpen,
        lastUpdate: data.timestamp
      }) : d));
    });
    socket.on('alert', (data) => {
      setDevices(prev => prev.map(d => d.id === data.deviceId ? ({ ...d }) : d));
    });
    return () => socket.close();
  }, []);

  const fetchDevices = async () => {
    try {
      const response = await api.get('/api/devices');
      setDevices(response.data.data);
    } catch (err) {
      setError('Σφάλμα κατά τη φόρτωση συσκευών');
    }
  };

  const fetchLocations = async () => {
    try {
      const response = await api.get('/api/locations');
      setLocations(response.data.data);
    } catch (err) {
      console.error('Error fetching locations:', err);
    }
  };

  const handleShowAlerts = (device) => {
    setAlertDeviceId(device.id);
    if (device.DeviceAlert) {
      setAlertData({
        minTemp: parseFloat(device.DeviceAlert.minTemp),
        maxTemp: parseFloat(device.DeviceAlert.maxTemp),
        alertsEnabled: device.DeviceAlert.alertsEnabled ?? false,
        doorAlertsEnabled: device.DeviceAlert.doorAlertsEnabled ?? false,
        doorOpenDelay: device.DeviceAlert.doorOpenDelay ?? 5,
        localAlarmThreshold: device.DeviceAlert.localAlarmThreshold ?? 3,
        localAlarmDurationSec: device.DeviceAlert.localAlarmDurationSec ?? 10,
        tempDelayEnabled: device.DeviceAlert.tempDelayEnabled ?? false,
        tempAlertDelay: device.DeviceAlert.tempAlertDelay ?? 15,
        emailAlertsEnabled: device.DeviceAlert.emailAlertsEnabled ?? false,
        discordAlertsEnabled: device.DeviceAlert.discordAlertsEnabled ?? false,
        reportIntervalSec: device.DeviceAlert.reportIntervalSec ?? 300 // Διαβάζει τη βάση
      });
    } else {
      setAlertData({ minTemp: 0, maxTemp: 10, alertsEnabled: false, doorAlertsEnabled: false, doorOpenDelay: 5, localAlarmThreshold: 3, localAlarmDurationSec: 10, tempDelayEnabled: false, tempAlertDelay: 15, emailAlertsEnabled: false, discordAlertsEnabled: false, reportIntervalSec: 300 });
    }
    setShowAlertModal(true);
  };

  const handleSaveAlerts = async (e) => {
      e.preventDefault();
      setLoading(true);
      try {
          await api.put(`/api/devices/${alertDeviceId}/alerts`, alertData);
          setSuccess('Οι ρυθμίσεις αποθηκεύτηκαν επιτυχώς');
          setShowAlertModal(false);
          fetchDevices();
      } catch (err) {
          setError('Σφάλμα κατά την αποθήκευση ειδοποιήσεων');
      } finally {
          setLoading(false);
      }
  };

  const handleShowModal = (device = null) => {
    if (device) {
      setEditingDevice(device);
      const currentLocId = device.locationId || (device.Location ? device.Location.id : '');
      setFormData({
        locationId: currentLocId ? String(currentLocId) : '',
        name: device.name || '',
        deviceType: device.deviceType || 'fridge',
        shellyId: device.shellyId || '',
        description: device.description || '',
        hardwareType: device.hardwareType || 'shelly',
        pinConfig: device.pinConfig || { sensorType: 'dallas', hasDoor: false }
      });
    } else {
      setEditingDevice(null);
      setFormData({
        locationId: '',
        name: '',
        deviceType: 'fridge',
        shellyId: '',
        description: '',
        hardwareType: 'shelly',
        pinConfig: { sensorType: 'dallas', hasDoor: false }
      });
    }
    setShowModal(true);
    setError('');
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingDevice(null);
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.locationId || formData.locationId === "") {
        setError('Παρακαλώ επιλέξτε τοποθεσία.');
        return;
    }
    if (!formData.name || formData.name.trim() === "") {
        setError('Το όνομα της συσκευής είναι υποχρεωτικό.');
        return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const cleanData = JSON.parse(JSON.stringify(formData));
      cleanData.locationId = parseInt(cleanData.locationId);
      
      if (!cleanData.pinConfig) {
          cleanData.pinConfig = { sensorType: 'dallas', hasDoor: false };
      }

      if (editingDevice) {
        await api.put(`/api/devices/${editingDevice.id}`, cleanData);
        setSuccess('Η συσκευή ενημερώθηκε επιτυχώς');
      } else {
        const response = await api.post('/api/devices', cleanData);
        setSuccess('Η συσκευή δημιουργήθηκε επιτυχώς');
        
        if (response.data && response.data.data && response.data.data.token) {
          setSelectedToken(response.data.data.token);
          setShowTokenModal(true);
        }
      }
      handleCloseModal();
      fetchDevices();
    } catch (err) {
      console.error("Σφάλμα αποθήκευσης:", err);
      const errMsg = err.response?.data?.error || err.message || 'Σφάλμα κατά την αποθήκευση';
      setError(errMsg);
      alert("Σφάλμα κατά την αποθήκευση:\n" + errMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Είστε σίγουροι ότι θέλετε να διαγράψετε αυτή τη συσκευή;')) return;
    try {
      await api.delete(`/api/devices/${id}`);
      setSuccess('Η συσκευή διαγράφηκε επιτυχώς');
      fetchDevices();
    } catch (err) {
      setError('Σφάλμα κατά τη διαγραφή');
    }
  };

  const handleRegenerateToken = async (device) => {
    if (!window.confirm('Αναδημιουργία Token; Το παλιό θα σταματήσει να λειτουργεί.')) return;
    try {
      const response = await api.post(`/api/devices/${device.id}/regenerate-token`);
      setSuccess('Το token αναδημιουργήθηκε επιτυχώς');
      setSelectedToken(response.data.data.token);
      setShowTokenModal(true);
      fetchDevices();
    } catch (err) {
      setError('Σφάλμα κατά την αναδημιουργία token');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setSuccess('Αντιγράφηκε στο clipboard');
    setTimeout(() => setSuccess(''), 3000);
  };

  const getDeviceTypeIcon = (type) => {
    switch (type) {
      case 'fridge': return 'bi-thermometer-half';
      case 'freezer': return 'bi-thermometer-snow';
      case 'room': return 'bi-house';
      default: return 'bi-thermometer';
    }
  };

  const getDeviceTypeLabel = (type) => {
    switch (type) {
      case 'fridge': return 'Ψυγείο';
      case 'freezer': return 'Καταψύκτης';
      case 'room': return 'Χώρος';
      default: return type;
    }
  };

  return (
    <Container fluid>
      <Row className="mb-4">
        <Col>
          <h2><i className="bi bi-thermometer me-2"></i>Διαχείριση Συσκευών</h2>
          {user.role === 'client_admin' && user.clientName && (
            <p className="text-muted mb-0">{user.clientName}</p>
          )}
        </Col>
        <Col className="text-end">
          <Button variant="primary" onClick={() => handleShowModal()}>
            <i className="bi bi-plus-circle me-1"></i>
            Νέα Συσκευή
          </Button>
        </Col>
      </Row>

      {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert variant="success" dismissible onClose={() => setSuccess('')}>{success}</Alert>}

      <Card>
        <Card.Body>
          <Table responsive hover>
            <thead>
              <tr>
                <th>ID</th>
                {user.role === 'super_admin' && <th>Client</th>}
                <th>Τοποθεσία</th>
                <th>Όνομα</th>
                <th>Hardware</th>
                <th>Τύπος</th>
                <th>Alerts</th>
                <th>Token</th>
                <th>Ενέργειες</th>
              </tr>
            </thead>
            <tbody>
              {devices.map(device => (
                <tr key={device.id}>
                  <td>{device.id}</td>
                  {user.role === 'super_admin' && (
                    <td>{device.Location?.Client?.name || '-'}</td>
                  )}
                  <td>{device.Location?.name || '-'}</td>
                  <td>
                    <i className={`bi ${getDeviceTypeIcon(device.deviceType)} me-2`}></i>
                    <strong>{device.name}</strong>
                  </td>
                  <td>
                      <Badge bg={device.hardwareType === 'esp32' ? 'dark' : 'primary'}>
                          {device.hardwareType === 'esp32' ? 'ESP32' : 'Shelly'}
                      </Badge>
                  </td>
                  <td><Badge bg="secondary">{getDeviceTypeLabel(device.deviceType)}</Badge></td>
                  <td>
                      {device.DeviceAlert && device.DeviceAlert.alertsEnabled ? (
                          <Badge bg="info" className="text-dark">
                              {device.DeviceAlert.minTemp}°C / {device.DeviceAlert.maxTemp}°C
                          </Badge>
                      ) : (<Badge bg="secondary">Disabled</Badge>)}
                  </td>
                  <td><code className="small">{device.token.substring(0, 8)}...</code></td>
                  <td>
                    <div className="d-flex gap-1">
                      <Button size="sm" variant="outline-dark" onClick={() => handleShowAlerts(device)} title="Ρυθμίσεις Ειδοποιήσεων"><i className="bi bi-bell"></i></Button>
                      <Button size="sm" variant="outline-primary" onClick={() => handleShowModal(device)} title="Επεξεργασία"><i className="bi bi-pencil"></i></Button>
                      <Button size="sm" variant="outline-warning" onClick={() => handleRegenerateToken(device)} title="Αναδημιουργία Token"><i className="bi bi-arrow-clockwise"></i></Button>
                      <Button size="sm" variant="outline-danger" onClick={() => handleDelete(device.id)} title="Διαγραφή"><i className="bi bi-trash"></i></Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
          {devices.length === 0 && <div className="text-center py-4 text-muted">Δεν υπάρχουν συσκευές.</div>}
        </Card.Body>
      </Card>

      {/* Create/Edit Modal */}
      <Modal show={showModal} onHide={handleCloseModal}>
        <Modal.Header closeButton>
          <Modal.Title>{editingDevice ? 'Επεξεργασία Συσκευής' : 'Νέα Συσκευή'}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Τοποθεσία *</Form.Label>
              <Form.Select 
                value={formData.locationId || ""} 
                onChange={(e) => setFormData({ ...formData, locationId: e.target.value })} 
              >
                <option value="" disabled>-- Επιλέξτε Τοποθεσία --</option>
                {locations.map(location => (
                  <option key={location.id} value={location.id}>
                    {location.name} {user.role === 'super_admin' && location.Client && ` (${location.Client.name})`}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Row>
              <Col md={8}>
                <Form.Group className="mb-3">
                  <Form.Label>Όνομα Συσκευής *</Form.Label>
                  <Form.Control type="text" placeholder="π.χ. Ψυγείο 1" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Τύπος *</Form.Label>
                  <Form.Select value={formData.deviceType} onChange={(e) => setFormData({ ...formData, deviceType: e.target.value })}>
                    <option value="fridge">Ψυγείο</option>
                    <option value="freezer">Καταψύκτης</option>
                    <option value="room">Χώρος</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3 bg-light p-2 border rounded">
                <Form.Label>Τύπος Συσκευής (Hardware)</Form.Label>
                <div className="d-flex gap-3">
                    <Form.Check type="radio" label="Shelly (Gen 2/Plus)" name="hwType" checked={formData.hardwareType === 'shelly'} onChange={() => setFormData({...formData, hardwareType: 'shelly'})} />
                    <Form.Check type="radio" label="ESP32 (Arduino)" name="hwType" checked={formData.hardwareType === 'esp32'} onChange={() => setFormData({...formData, hardwareType: 'esp32'})} />
                </div>
            </Form.Group>

            {formData.hardwareType === 'esp32' ? (
                <div className="mb-3 p-2 border border-warning rounded">
                    <Row>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label className="small">Τύπος Αισθητήρα</Form.Label>
                            <Form.Select 
                              value={formData.pinConfig?.sensorType || 'dallas'} 
                              onChange={(e) => setFormData({
                                ...formData, 
                                pinConfig: { ...(formData.pinConfig || {}), sensorType: e.target.value }
                              })}
                            >
                              <option value="dallas">Dallas (DS18B20)</option>
                              <option value="dht11">DHT11</option>
                              <option value="dht21">DHT21</option>
                            </Form.Select>
                          </Form.Group>
                        </Col>
                        <Col md={6} className="d-flex align-items-center">
                            <Form.Check 
                                type="checkbox" 
                                label="Έχει αισθητήρα πόρτας;" 
                                checked={formData.pinConfig?.hasDoor || false} 
                                onChange={e=>setFormData({...formData, pinConfig:{...formData.pinConfig, hasDoor:e.target.checked}})} 
                            />
                        </Col>
                    </Row>
                </div>
            ) : (
                <div className="mb-3 p-2 border border-primary rounded">
                    <Form.Check 
                        type="checkbox" 
                        label="Έχει αισθητήρα πόρτας (Addon);" 
                        checked={formData.pinConfig?.hasDoor || false} 
                        onChange={e=>setFormData({...formData, pinConfig:{...formData.pinConfig, hasDoor:e.target.checked}})} 
                    />
                    <Form.Group className="mt-2">
                        <Form.Label className="small">Shelly ID</Form.Label>
                        <Form.Control type="text" placeholder="π.χ. 192.168.1.100" value={formData.shellyId} onChange={(e) => setFormData({ ...formData, shellyId: e.target.value })} />
                    </Form.Group>
                </div>
            )}

            <Form.Group className="mb-3">
              <Form.Label>Περιγραφή</Form.Label>
              <Form.Control as="textarea" rows={2} value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} />
            </Form.Group>

            {editingDevice && <Alert variant="info"><small><strong>Token:</strong> {editingDevice.token}</small></Alert>}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModal}>Ακύρωση</Button>
            <Button variant="primary" type="submit" disabled={loading}>{loading ? 'Αποθήκευση...' : 'Αποθήκευση'}</Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Token Modal */}
      <Modal show={showTokenModal} onHide={() => setShowTokenModal(false)}>
        <Modal.Header closeButton><Modal.Title>Σύνδεση Συσκευής</Modal.Title></Modal.Header>
        <Modal.Body>
          <Alert variant="warning"><strong>Σημαντικό!</strong> Αποθηκεύστε αυτό το token. Χρησιμοποιήστε το στο Firmware της συσκευής.</Alert>
          <Form.Group>
            <InputGroup>
              <Form.Control type="text" value={selectedToken} readOnly />
              <Button variant="outline-secondary" onClick={() => copyToClipboard(selectedToken)}><i className="bi bi-clipboard"></i> Αντιγραφή</Button>
            </InputGroup>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={() => setShowTokenModal(false)}>Κλείσιμο</Button>
        </Modal.Footer>
      </Modal>

      {/* Alert Settings Modal - ΠΡΟΣΤΕΘΗΚΕ ΤΟ INTERVAL */}
      <Modal show={showAlertModal} onHide={() => setShowAlertModal(false)}>
          <Modal.Header closeButton><Modal.Title><i className="bi bi-gear me-2"></i>Ρυθμίσεις Συσκευής</Modal.Title></Modal.Header>
          <Form onSubmit={handleSaveAlerts}>
              <Modal.Body>
                  {/* === GROUP 0: ΣΥΧΝΟΤΗΤΑ (INTERVAL) === */}
                  <div className="mb-3 border-bottom pb-3">
                      <Row className="align-items-center">
                        <Col xs={7} md={8}>
                          <span className="fw-bold"><i className="bi bi-clock-history me-2 text-primary"></i>Συχνότητα (ESP32)</span>
                          <div className="small text-muted mt-1">Ανά πόσα λεπτά στέλνει δεδομένα; (Ελάχιστο: 1)</div>
                        </Col>
                        <Col xs={5} md={4} className="d-flex justify-content-end">
                          <InputGroup size="sm" style={{ width: '110px' }}>
                            <Form.Control 
                              type="number" 
                              min="1" 
                              // Διαιρούμε με το 60 για να δείχνουμε λεπτά στο χρήστη
                              value={alertData.reportIntervalSec ? alertData.reportIntervalSec / 60 : 5} 
                              onChange={(e) => {
                                let mins = parseInt(e.target.value);
                                if (isNaN(mins) || mins < 1) mins = 1;
                                // Πολλαπλασιάζουμε με το 60 για να το σώσουμε σε δευτερόλεπτα στη βάση
                                setAlertData({...alertData, reportIntervalSec: mins * 60});
                              }}
                            />
                            <InputGroup.Text>λεπτά</InputGroup.Text>
                          </InputGroup>
                        </Col>
                      </Row>
                  </div>

                  {/* === GROUP 1: ΘΕΡΜΟΚΡΑΣΙΑ === */}
                  <div className="mb-3 border-bottom pb-3">
                      <Form.Check type="switch" label="Ειδοποιήσεις Θερμοκρασίας" checked={alertData.alertsEnabled} onChange={(e) => setAlertData({...alertData, alertsEnabled: e.target.checked})} className="fw-bold mb-2 text-danger"/>
                      <Row className="mb-3">
                          <Col md={6}><Form.Group><Form.Label className="small text-muted">Min (°C)</Form.Label><Form.Control type="number" step="0.1" value={alertData.minTemp} onChange={(e) => setAlertData({...alertData, minTemp: e.target.value})} disabled={!alertData.alertsEnabled}/></Form.Group></Col>
                          <Col md={6}><Form.Group><Form.Label className="small text-muted">Max (°C)</Form.Label><Form.Control type="number" step="0.1" value={alertData.maxTemp} onChange={(e) => setAlertData({...alertData, maxTemp: e.target.value})} disabled={!alertData.alertsEnabled}/></Form.Group></Col>
                      </Row>
                      
                      <Row className="align-items-center">
                        <Col xs={8} md={9}>
                          <span className="small">Καθυστέρηση Ειδοποίησης (Λεπτά)</span>
                        </Col>
                        <Col xs={4} md={3} className="d-flex justify-content-end">
                          <InputGroup style={{width: '100px'}}>
                            <Form.Control size="sm" type="number" min="0" value={alertData.tempAlertDelay} onChange={(e) => setAlertData({...alertData, tempAlertDelay: e.target.value, tempDelayEnabled: true})} disabled={!alertData.alertsEnabled} />
                          </InputGroup>
                        </Col>
                      </Row>
                  </div>

                  {/* === GROUP 2: ΠΟΡΤΑ === */}
                  <Row className="mb-4 align-items-center">
                    <Col xs={8} md={9}>
                      <Form.Check
                        type="switch"
                        id={`doorAlertsSwitch-${alertDeviceId}`}
                        label={<span className="fw-bold text-warning">Ειδοποιήσεις Πόρτας</span>}
                        checked={alertData.doorAlertsEnabled}
                        onChange={(e) => setAlertData({...alertData, doorAlertsEnabled: e.target.checked})}
                      />
                      <div className="small text-muted mt-1">Ανοχή ανοιχτής πόρτας (σε λεπτά):</div>
                    </Col>
                    <Col xs={4} md={3} className="d-flex justify-content-end align-items-end">
                      <InputGroup style={{width: '100px'}}>
                        <Form.Control size="sm" type="number" min="0" value={alertData.doorOpenDelay} onChange={(e) => setAlertData({...alertData, doorOpenDelay: e.target.value})} disabled={!alertData.doorAlertsEnabled} />
                      </InputGroup>
                    </Col>
                  </Row>

                  {/* === GROUP 3: EMAIL & DISCORD TOGGLES === */}
                  <Row className="mt-2 bg-light p-3 rounded">
                    <Col xs={12} className="d-flex justify-content-around align-items-center">
                      <Form.Check type="switch" id={`emailAlertsSwitch-${alertDeviceId}`} label="Email Alerts" checked={alertData.emailAlertsEnabled} onChange={(e) => setAlertData({...alertData, emailAlertsEnabled: e.target.checked})} />
                      <Form.Check type="switch" id={`discordAlertsSwitch-${alertDeviceId}`} label="Discord Alerts" checked={alertData.discordAlertsEnabled} onChange={(e) => setAlertData({...alertData, discordAlertsEnabled: e.target.checked})} />
                    </Col>
                  </Row>
              </Modal.Body>
              <Modal.Footer>
                  <Button variant="secondary" onClick={() => setShowAlertModal(false)}>Ακύρωση</Button>
                  <Button variant="success" type="submit" disabled={loading}>Αποθήκευση</Button>
              </Modal.Footer>
          </Form>
      </Modal>
    </Container>
  );
}

export default Devices;